// src/components/appointments/BookAppointmentModal.tsx
// Modal for receptionist/admin to book a new appointment.
// Flow: pick doctor → pick/create slot → pick patient → submit.
import React, { useState, useEffect } from "react";
import { DateInput } from "../ui/DateInput";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AppointmentAPI, isWaitlistEntry } from "../../services/appointment.service";
import { db } from "../../db/schema";
import { putAndQueue } from "../../db/operations";
import type { AppointmentRecord } from "../../db/db.types";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../../utils/network";
import { DoctorAPI } from "../../services/doctor.service";
import type { DoctorListItem } from "../../services/doctor.service";
import { PatientAPI, getPatientByDisplayId } from "../../services/patient.service";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import { useAppointmentConfig } from "../../hooks/useAppointmentConfig";
import { useRole } from "../../hooks/useRole";
import { getCurrentUser } from "aws-amplify/auth";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Pre-fill doctor (e.g. opened from doctor queue view) */
  prefillDoctorId?: string;
  /** Pre-fill date */
  prefillDate?: string;
  /** Pre-fill patient by display_id or UUID (e.g. navigating from patient profile) */
  prefillPatientId?: string;
}

/** Safely get a phone number from the patient record */
const getPhone = (p: PatientDbRecord): string =>
  p.contact_number ?? p.contact?.contact_number ?? "";

export const BookAppointmentModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  prefillDoctorId = "",
  prefillDate = "",
  prefillPatientId = "",
}) => {
  const navigate = useNavigate();
  const clinicConfig = useAppointmentConfig();
  const { role, canOr } = useRole();
  // Last line of defence: every entry point hides its Book button without the
  // grant, but the modal is also reachable from a URL (?open_booking=true) and
  // a grant can be revoked while a page is open. POST /appointments 403s anyway.
  const canBook = canOr("appointments.create", true);
  const [step, setStep] = useState<"slot" | "patient">("slot");
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [patients, setPatients] = useState<PatientDbRecord[]>([]);
  const [slots, setSlots] = useState<import("../../services/appointment.service").AppointmentSlot[]>([]);

  // Form fields
  const [doctorId, setDoctorId] = useState(prefillDoctorId);
  const [date, setDate] = useState(prefillDate || new Date().toISOString().slice(0, 10));
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [patientId, setPatientId] = useState("");
  const [notes, setNotes] = useState("");
  const [patientSearch, setPatientSearch] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [slotSearch, setSlotSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  // Live bookings for the selected doctor on the selected date — summed from the
  // day's slot booked_counts. Drives the per-doctor daily cap below.
  const [bookedToday, setBookedToday] = useState(0);

  // Per-doctor daily booking cap for the selected date. The doctor's own
  // schedule (set in Roster) wins; otherwise fall back to the clinic-wide
  // default from Settings. slot_duration_minutes is shown as a hint.
  const selectedDoctor = doctors.find(d => d.doctor_id === doctorId);
  // Doctors can only book for themselves: restrict the dropdown to their own
  // record. Non-doctors (Admin / Receptionist) see the full clinic list. While
  // the role is still unknown (null), default to the full list — no behaviour
  // change for existing flows.
  const isDoctor = role === "Doctor";
  const myDoctor = isDoctor
    ? doctors.find(d => d.user_id === currentUserId || d.doctor_id === currentUserId)
    : undefined;
  const visibleDoctors = isDoctor ? (myDoctor ? [myDoctor] : []) : doctors;
  // JS getDay() is 0=Sun..6=Sat; availability schedules use 0=Mon..6=Sun.
  const dayIndex = date ? (new Date(`${date}T00:00:00`).getDay() + 6) % 7 : -1;
  const daySchedule = selectedDoctor?.availability_schedules?.find(s => s.day_of_week === dayIndex);
  const effectiveSlotMinutes = daySchedule?.slot_duration_minutes ?? clinicConfig.slot_duration_minutes;
  const dailyCap = daySchedule?.max_daily_bookings ?? clinicConfig.max_daily_bookings;
  const remainingToday = Math.max(0, dailyCap - bookedToday);
  const capReached = bookedToday >= dailyCap;

  // Load doctors on open — persists to localStorage so offline opens still show the list
  useEffect(() => {
    if (!isOpen) return;
    getCurrentUser().then(u => setCurrentUserId(u.userId)).catch(() => {});
    DoctorAPI.list({ is_active: true })
      .then(data => {
        setDoctors(data);
        try { localStorage.setItem("kgz_doctors", JSON.stringify(data)); } catch {}
      })
      .catch((err) => {
        // A 403 proves we are online and were refused — never answer it from cache.
        if (isPermissionDenied(err)) return;
        try {
          const cached = localStorage.getItem("kgz_doctors");
          if (cached) setDoctors(JSON.parse(cached) as DoctorListItem[]);
        } catch {}
      });

    // Load patients:
    // Online  → fetch from server (server UUIDs), save to localStorage cache.
    // Offline → use localStorage cache (full server list, correct UUIDs) plus any
    //           Dexie patients still in "pending" state (created offline this session,
    //           not yet synced — can safely be booked together with their appointment).
    //           Deliberately exclude "synced" Dexie patients: if remapping ran their
    //           record was updated to the server UUID and they appear in the cache; if
    //           remapping failed the Dexie record still holds a stale temp UUID that
    //           would cause a FK violation when the appointment is pushed.
    PatientAPI.getAll()
      .then(async (data) => {
        // Cache the authoritative server list.
        try { localStorage.setItem("kgz_patients", JSON.stringify(data)); } catch {}

        // Also surface any Dexie patients still in "pending" state — they were
        // created offline and the push engine may not have synced them yet
        // (e.g. user comes back online and opens this modal within <30s before
        // the first push cycle completes). Without this merge, the patient the
        // receptionist just created appears to be missing for up to 30 seconds.
        // Cache only the server list; the pending rows are session-only.
        try {
          const pending = await db.patients
            .filter(p => (p as unknown as { sync_status: string }).sync_status === "pending")
            .toArray();
          const knownIds = new Set(data.map(p => p.patient_id));
          const extras = pending
            .filter(p => !knownIds.has(p.patient_id as string))
            .map(p => p as unknown as PatientDbRecord);
          setPatients(extras.length > 0 ? [...data, ...extras] : data);
        } catch {
          setPatients(data);
        }
      })
      .catch(async (err) => {
        // patients.view refused: the cached list below is exactly the PHI the
        // grant was revoked for, so drop it instead of rendering it — and clear
        // the plaintext copy so it does not outlive the revocation on this device.
        if (isPermissionDenied(err)) {
          try { localStorage.removeItem("kgz_patients"); } catch {}
          setPatients([]);
          toast.error(actionErrorMessage(err, "Failed to load patients"));
          return;
        }
        const merged: PatientDbRecord[] = [];

        // 1. Cached server patients (full list, correct server UUIDs).
        try {
          const raw = localStorage.getItem("kgz_patients");
          if (raw) merged.push(...(JSON.parse(raw) as PatientDbRecord[]));
        } catch {}

        // 2. Dexie patients that are still pending (just created offline this session).
        //    These don't exist on the server yet, but will be pushed before the
        //    appointment — remapQueueReferences will fix the appointment's FK once
        //    the patient sync completes.
        try {
          const pending = await db.patients
            .filter(p => (p as unknown as { sync_status: string }).sync_status === "pending")
            .toArray();
          const knownIds = new Set(merged.map(p => p.patient_id));
          for (const p of pending) {
            if (!knownIds.has(p.patient_id as string)) {
              merged.push(p as unknown as PatientDbRecord);
            }
          }
        } catch {}

        setPatients(merged);
      });
  }, [isOpen]);

  // Sync page-level doctor filter into the modal each time it opens.
  // prefillDoctorId="" means no filter active → reset dropdown to placeholder.
  // Runs only on isOpen transition, not on every parent re-render.
  useEffect(() => {
    if (!isOpen) return;
    setDoctorId(prefillDoctorId);
    setSelectedSlotId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Doctors: auto-select their own record once the doctor list + identity load.
  useEffect(() => {
    if (!isDoctor || prefillDoctorId) return;
    if (myDoctor && doctorId !== myDoctor.doctor_id) {
      setDoctorId(myDoctor.doctor_id);
      setSelectedSlotId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoctor, myDoctor, prefillDoctorId]);

  // Auto-select patient when navigating from PatientPage (prefillPatientId is display_id or UUID)
  useEffect(() => {
    if (!isOpen || !prefillPatientId) return;

    const match = patients.find(
      p => p.display_id === prefillPatientId || p.patient_id === prefillPatientId
    );

    if (match?.patient_id) {
      setPatientId(match.patient_id);
      setStep("slot");
      return;
    }

    // Patient not in the loaded list (paginated list may not include them) — fetch directly
    const isDisplayId = prefillPatientId.toUpperCase().startsWith("PAT-");
    const fetch = isDisplayId
      ? getPatientByDisplayId(prefillPatientId)
      : PatientAPI.getById(prefillPatientId);

    fetch
      .then((found) => {
        if (found?.patient_id) {
          setPatients((prev) => {
            if (prev.some((p) => p.patient_id === found.patient_id)) return prev;
            return [...prev, found];
          });
          setPatientId(found.patient_id);
          setStep("slot");
        }
      })
      .catch(() => {
        // Patient not found — let the user pick manually, no toast needed
      });
  }, [isOpen, prefillPatientId, patients]);

  // Load slots when doctor + date selected.
  // Persists to localStorage per (doctor, date) so the list survives offline re-opens.
  useEffect(() => {
    if (!doctorId || !date) { setSlots([]); setBookedToday(0); return; }
    setSlotsLoading(true);
    const cacheKey = `kgz_slots_${doctorId}_${date}`;
    const now = new Date();
    // Sum across the whole day's slots (incl. past) — the cap is per-day, not per-slot.
    const countBooked = (day: typeof slots) => day.reduce((n, s) => n + (s.booked_count || 0), 0);
    AppointmentAPI.listSlots({ doctor_id: doctorId, date: new Date(date).toISOString() })
      .then((all) => {
        setSlots(all.filter(s => new Date(s.start_time) > now));
        setBookedToday(countBooked(all));
        try { localStorage.setItem(cacheKey, JSON.stringify(all)); } catch {}
      })
      .catch((err) => {
        if (isPermissionDenied(err)) { setSlots([]); setBookedToday(0); return; }
        try {
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const day = JSON.parse(cached) as typeof slots;
            setSlots(day.filter(s => new Date(s.start_time) > now));
            setBookedToday(countBooked(day));
          } else { setSlots([]); setBookedToday(0); }
        } catch { setSlots([]); setBookedToday(0); }
      })
      .finally(() => setSlotsLoading(false));
  }, [doctorId, date]);

  // Online prefetch: when the modal is open and we have the doctor list,
  // grab the next 14 days of slots for every doctor and seed the per-(doctor, date)
  // localStorage cache. That way, picking any doctor + any date while offline
  // shows the schedule instead of an empty list. Cancellable on close/unmount.
  useEffect(() => {
    if (!isOpen || !doctors.length || !navigator.onLine) return;
    let cancelled = false;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const end = new Date(today); end.setDate(end.getDate() + 14);

    (async () => {
      for (const doc of doctors) {
        if (cancelled) return;
        try {
          const all = await AppointmentAPI.listSlots({
            doctor_id:  doc.doctor_id,
            start_date: today.toISOString(),
            end_date:   end.toISOString(),
          });
          if (cancelled) return;

          // Group by YYYY-MM-DD and write each bucket under the same key
          // shape used by the per-(doctor, date) lookup above.
          const byDate = new Map<string, typeof all>();
          for (const s of all) {
            const key = new Date(s.start_time).toISOString().slice(0, 10);
            const bucket = byDate.get(key) ?? [];
            bucket.push(s);
            byDate.set(key, bucket);
          }
          for (const [day, bucket] of Array.from(byDate.entries())) {
            try { localStorage.setItem(`kgz_slots_${doc.doctor_id}_${day}`, JSON.stringify(bucket)); } catch {}
          }
        } catch { /* one doctor failing shouldn't block the rest */ }
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, doctors]);

  const resetAndClose = () => {
    setStep("slot");
    setDoctorId(prefillDoctorId);
    setDate(prefillDate || new Date().toISOString().slice(0, 10));
    setSelectedSlotId("");
    setPatientId("");
    setNotes("");
    setSlotSearch("");
    onClose();
  };

  const handleBook = async () => {
    if (!patientId) { toast.error("Select a patient"); return; }
    if (!selectedSlotId) { toast.error("Select a slot"); return; }
    if (capReached) {
      toast.error(`Daily booking limit reached for this doctor (${dailyCap} per day)`);
      return;
    }
    setLoading(true);
    try {
      const result = await AppointmentAPI.book({
        patient_id: patientId,
        doctor_id: doctorId,
        slot_id: selectedSlotId,
        notes: notes || undefined,
      });
      if (isWaitlistEntry(result)) {
        toast.info(`Slot is full — added to waitlist at position #${result.position}`);
        onSuccess();
        resetAndClose();
      } else {
        onSuccess();
        resetAndClose();
        navigate(`/appointments/confirmation/${result.appointment_id}`);
      }
    } catch (e: any) {
      // Refused, not offline: queueing it would report "saved offline" for a
      // booking the server has already said this user may not make.
      if (isPermissionDenied(e)) {
        toast.error(actionErrorMessage(e, "Failed to book appointment"));
        return;
      }
      if (isBackendUnreachable(e)) {
        try {
          const newApptId = crypto.randomUUID();
          const selectedSlot = slots.find(s => s.slot_id === selectedSlotId);
          await putAndQueue<AppointmentRecord>(
            "appointment",
            newApptId,
            {
              appointment_id:   newApptId,
              patient_id:       patientId,
              doctor_id:        doctorId,
              slot_id:          selectedSlotId,
              appointment_date: date,
              start_time:       selectedSlot?.start_time ?? date,
              end_time:         selectedSlot?.end_time ?? null,
              status:           "Scheduled",
              notes:            notes || null,
            },
            "create",
          );
          toast.success("Appointment saved offline — will sync when connected");
          onSuccess();
          resetAndClose();
        } catch {
          toast.error("Failed to save offline. Please try again.");
        }
        return;
      }
      toast.error(e.message || "Failed to book appointment");
    } finally {
      setLoading(false);
    }
  };

  const filteredPatients = patients.filter((p) => {
    const q = patientSearch.toLowerCase();
    const phone = getPhone(p);
    return (
      !q ||
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) ||
      phone.includes(q)
    );
  });

  const fmt = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Match the search against the slot's start time (spaces/case-insensitive) so
  // "540", "5:40" or "pm" all narrow a long list down quickly.
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, "");
  const visibleSlots = slotSearch.trim()
    ? slots.filter((s) => norm(fmt(s.start_time)).includes(norm(slotSearch)))
    : slots;

  if (!isOpen || !canBook) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Book Appointment</h2>
          <button onClick={resetAndClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold">×</button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {step === "slot" && (
            <>
              {/* Doctor */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Doctor *</label>
                <select
                  value={doctorId}
                  onChange={(e) => { setDoctorId(e.target.value); setSelectedSlotId(""); }}
                  disabled={isDoctor && !!myDoctor}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
                >
                  {!(isDoctor && myDoctor) && <option value="">Select doctor</option>}
                  {visibleDoctors.map((d) => (
                    <option key={d.doctor_id} value={d.doctor_id}>
                      {d.name} — {d.specialization}
                    </option>
                  ))}
                </select>
                {isDoctor && (
                  <p className="mt-1 text-[11px] text-slate-400">
                    You can only book appointments for yourself.
                  </p>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Date *</label>
                <DateInput
                  value={date}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => { setDate(e.target.value); setSelectedSlotId(""); setSlotSearch(""); }}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                />
              </div>

              {/* Slot picker */}
              {doctorId && date && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-slate-500">Available Slots *</label>
                    <span className="text-[11px] text-slate-400">{effectiveSlotMinutes}-min slots</span>
                  </div>

                  {(slotsLoading || slots.length > 0) && (capReached ? (
                    <div className="mb-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs font-medium text-red-600">
                      Daily booking limit reached for this doctor ({dailyCap}/day). Please choose another day.
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 mb-2">
                      {remainingToday} of {dailyCap} booking{dailyCap !== 1 ? "s" : ""} left for this day
                    </p>
                  ))}

                  {slotsLoading ? (
                    <div className="grid grid-cols-3 gap-2 py-1">
                      {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-9 rounded-lg bg-slate-100 animate-pulse" />)}
                    </div>
                  ) : slots.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2">No slots for this date. Ask an admin to set up a schedule for this doctor.</p>
                  ) : (
                    <>
                      {/* Quick time search keeps long slot lists navigable */}
                      <input
                        type="text"
                        value={slotSearch}
                        onChange={(e) => setSlotSearch(e.target.value)}
                        placeholder="Search slot..."
                        className="w-full mb-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                      />
                      {visibleSlots.length === 0 ? (
                        <p className="text-xs text-slate-400 py-2">No slots match “{slotSearch}”.</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-0.5">
                          {visibleSlots.map((slot) => {
                            const full = slot.booked_count >= slot.capacity;
                            const disabled = slot.is_blocked || full || capReached;
                            const selected = selectedSlotId === slot.slot_id;
                            return (
                              <button
                                key={slot.slot_id}
                                onClick={() => !disabled && setSelectedSlotId(slot.slot_id)}
                                disabled={disabled}
                                title={slot.is_blocked ? "Blocked" : full ? "Full" : "Available"}
                                className={`rounded-lg border px-2 py-2 text-center transition ${
                                  selected
                                    ? "border-cyan-500 bg-cyan-50 text-cyan-700 shadow-sm"
                                    : disabled
                                    ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                                    : "border-slate-200 hover:border-cyan-300 hover:bg-cyan-50/50 text-slate-700"
                                }`}
                              >
                                <span className="block text-sm font-semibold">{fmt(slot.start_time)}</span>
                                {(slot.is_blocked || full) && (
                                  <span className="block text-[10px] font-medium mt-0.5">
                                    {slot.is_blocked ? "Blocked" : "Full"}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Reason for visit…"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400"
                />
              </div>

              <button
                disabled={!doctorId || !selectedSlotId || capReached}
                onClick={() => setStep("patient")}
                className="w-full bg-cyan-500 text-white py-2.5 rounded-xl font-medium text-sm hover:bg-cyan-600 disabled:opacity-40"
              >
                Next: Select Patient →
              </button>
            </>
          )}

          {step === "patient" && (
            <>
              <button onClick={() => setStep("slot")} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 mb-2">
                ← Back to Slot
              </button>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Search Patient</label>
                <input
                  type="text"
                  placeholder="Name or phone…"
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                />
              </div>

              <div className="space-y-1 max-h-64 overflow-y-auto">
                {filteredPatients.slice(0, 30).map((p) => {
                  const pid = p.patient_id as string | undefined;
                  const phone = getPhone(p);
                  return (
                    <button
                      key={pid}
                      onClick={() => { if (pid) setPatientId(pid); }}
                      className={`w-full rounded-xl border px-4 py-2.5 text-left text-sm transition ${
                        patientId === pid
                          ? "border-cyan-500 bg-cyan-50 text-cyan-700"
                          : "border-slate-100 hover:border-cyan-200 text-slate-700"
                      }`}
                    >
                      <span className="font-medium">{p.first_name} {p.last_name}</span>
                      {phone && (
                        <span className="ml-2 text-xs text-slate-400">{phone}</span>
                      )}
                    </button>
                  );
                })}
                {filteredPatients.length === 0 && (
                  <p className="text-xs text-slate-400 py-2">No patients found.</p>
                )}
              </div>

              <button
                disabled={!patientId || loading}
                onClick={handleBook}
                className="w-full bg-cyan-500 text-white py-2.5 rounded-xl font-medium text-sm hover:bg-cyan-600 disabled:opacity-40"
              >
                {loading ? "Booking…" : "Confirm Booking"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};